/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLocation, useNavigate } from 'react-router-dom'
import { animateScroll } from 'react-scroll'
import { useDispatch } from 'react-redux'
import constants, { capitalise, popularSkillsFromCategories, currentPopularSkills, currentPopularSkillsCount } from '../../utilities/constants'
import { setSavedResponse, setShowCourseLoader, setUserSkillName, setUTMDetails, setVisitedAnalysisPage, trackUserVisit } from '../../redux/skillSlice'
import { toast } from 'react-toastify'
import { Mixpanel } from '../../config/Mixpanel'
import useWindowSize from '../../hooks/useWindowSize'
import { resetAnalysis } from '../../redux/analysisSlice'
import { resetQuestionAnswer } from '../../redux/questionAnswerSlice'
import { resetProjectsData } from '../../redux/projectSlice'
import { resetCourseData } from '../../redux/coursesSlice'
import { resetXanoData } from '../../redux/beforePaymentSlice'
import { setAnalysisPageButtonClicked, setLastVisitedUrl } from '../../redux/nameSlice'
import { resetCurationStatus } from '../../redux/curatedCourseSlice'
import { resetQuestions } from '../../redux/questionsSlice'
import { resetSavedDetails } from '../../redux/savedCourseSlice'
import { fetchPopularSkills } from '../../redux/searchPopularSkillSlice'
import useDebounce from '../../hooks/useDebounce'
import SearchSkeleton from '../../components/SearchSkeleton'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { ArrowRight, ArrowUpRight, LoaderCircle } from 'lucide-react'
import { fetchUserInitSession } from '../../redux/onboardingUserDetails'
import { persistor, resetStore } from '../../store'
import { v4 as uuidv4 } from 'uuid';

const fallbackSuggestions = [
    'How to become a Project Manager',
    'Learn AI tools like ChatGPT and Claude',
    'Become a HR Manager',
]


const LandingPageV2 = () => {

    // Function to format numbers with K suffix
    const formatSkillCount = (count) => {
        if (!count) return '0'
        if (count < 1000) return count.toString()

        const thousands = count / 1000
        // If it's a whole number of thousands, show without decimal
        if (thousands >= 10) return Math.floor(thousands) + 'K'
        // For numbers like 1400 -> 1.4K, 2300 -> 2.3K
        return (Math.round(thousands * 10) / 10) + 'K'
    }

    const location = useLocation()

    const params = new URLSearchParams(location.search)
    const utm_source = params.get('utm_source')
    const utm_campaign = params.get('utm_campaign')
    const utm_medium = params.get('utm_medium')
    const skill_slug = params.get('skill_slug')
    const course_slug = params.get('course_slug')

    const inputRef = useRef(null)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { height, width } = useWindowSize()
    const defaultSlicingIndex = width <= 768 ? height >= 844 ? 10 : 5 : 15

    const [goalValue, setGoalValue] = useState('')
    const [popularSkills, setPopularSkills] = useState([])
    const [selectedSkill, setSelectedSkill] = useState([])
    const [searchResult, setSearchResult] = useState([])
    const [slicingIndex, setSlicingIndex] = useState(defaultSlicingIndex);
    const [scrollToTop, setScrollToTop] = useState(false)
    const [showTitle, setShowTitle] = useState(true)
    const [isUnsupportedCourse, setIsUnsupportedCourse] = useState(false)
    const [unsupportedCourseMessage, setUnsupportedCourseMessage] = useState('')
    const [vagueCourseSuggestions, setVagueCourseSuggestions] = useState([])
    const [rejectedGoalValue, setRejectedGoalValue] = useState('')
    const [unsupportedAttempt, setUnsupportedAttempt] = useState(0)
    const [isStartingSession, setIsStartingSession] = useState(false)
    const [step, setStep] = useState(0)
    const { debouncedValue } = useDebounce(goalValue)


    useEffect(() => {
        if (!localStorage.getItem('identification_id')) {
            const uuid_generated = uuidv4();
            localStorage.setItem('identification_id', uuid_generated);
            dispatch(trackUserVisit({ visitor_uuid: uuid_generated, page_path: utm_source }))
        }
        else {
            const identification_id = localStorage.getItem('identification_id');
            dispatch(trackUserVisit({ visitor_uuid: identification_id, page_path: utm_source }))
        }
        // eslint-disable-next-line no-undef


        return () => {
            localStorage.removeItem('identification_id');
        }
    }, []);


    useEffect(() => {
        Mixpanel.track(constants.Mixpanel.LANDING_PAGE)
        localStorage.setItem('showScholarship', JSON.stringify({}))
        localStorage.setItem('visitedAnalysisPage', JSON.stringify(false))
        // Reset redux state when user lands on base route
        dispatch(resetStore())
        persistor.purge()
        dispatch(resetQuestions())
        dispatch(resetAnalysis())
        dispatch(resetQuestionAnswer())
        dispatch(resetProjectsData())
        dispatch(resetCourseData())
        dispatch(resetXanoData())
        dispatch(setAnalysisPageButtonClicked(false))
        dispatch(resetCurationStatus())
        dispatch(resetSavedDetails())
        dispatch(setShowCourseLoader(false))
        dispatch(setVisitedAnalysisPage(false))
        dispatch(setSavedResponse(false))
        dispatch(setUTMDetails({ utm_source: utm_source ? utm_source : "", utm_medium: utm_medium ? utm_medium : "", utm_campaign: utm_campaign ? utm_campaign : "" }))
        dispatch(setLastVisitedUrl('/'))
        if (skill_slug) {
            const fetchData = async () => {
                try {
                    await setGoalValue(skill_slug)
                    const res = await dispatch(fetchPopularSkills({ searchValue: skill_slug }))
                    setPopularSkills(res?.payload)
                } catch (error) {
                    console.log(error)
                }
            }
            fetchData()
        }

        if (course_slug) {
            setPopularSkills(popularSkillsFromCategories.hasOwnProperty(course_slug) ? popularSkillsFromCategories[course_slug]?.skills : currentPopularSkills)
            setSelectedSkill(Array(popularSkillsFromCategories.hasOwnProperty(course_slug) ? popularSkillsFromCategories[course_slug]?.skills?.length : currentPopularSkills.length).fill(false))
        }

        if (!skill_slug && !course_slug) {
            if (inputRef.current) {
                inputRef.current.focus()
            }
            setPopularSkills(currentPopularSkills?.slice(0, 20))
        }

    }, [])

    useEffect(() => {
        if (!showTitle && goalValue.length > 0 && !scrollToTop) {
            setScrollToTop(true)
        }
    }, [goalValue, showTitle])

    useEffect(() => {
        if (scrollToTop) {
            animateScroll.scrollMore(height <= 740 ? 80 : 250, { duration: 300 })
        } else {
            animateScroll.scrollToTop({ duration: 300 })
        }
    }, [scrollToTop])

    useEffect(() => {
        if (debouncedValue) {
            const fetchData = async () => {
                try {
                    const res = await dispatch(fetchPopularSkills({ searchValue: debouncedValue }))
                    if (res?.payload?.length > 0) {
                        setSearchResult(res?.payload)
                    } else {
                        setSearchResult([debouncedValue])
                    }
                } catch (error) {
                    console.log(error)
                }
            }
            fetchData()
        } else {
            setSearchResult([])
        }
    }, [debouncedValue])

    useEffect(() => {
        if (step === 1) {
            animateScroll.scrollToTop({ duration: 300 })
        }
    }, [step])

    const handleSubmit = async (e) => {
        e.stopPropagation()
        e.preventDefault()

        if (goalValue === '') {
            toast.error('Type a skill you want to learn')
            return
        }

        const skillName = capitalise(goalValue)
        dispatch(setUserSkillName({ skillName }))
        setIsStartingSession(true)

        try {
            await dispatch(fetchUserInitSession({ anon_id: "", skill_name: skillName, role: "" })).unwrap()
        } catch (error) {
            const subjectValidationMessage = typeof error?.detail === 'string'
                ? error.detail
                : error?.detail?.message
            const subjectValidationMessages = ['Subject name not valid', 'Subject name too vague']

            if (error?.status === 400 && subjectValidationMessages.includes(subjectValidationMessage)) {
                const isVagueSubject = subjectValidationMessage === 'Subject name too vague'
                const suggestions = error?.detail?.suggestions
                const hasValidSuggestions = Array.isArray(suggestions)
                    && suggestions.length === 3
                    && suggestions.every((suggestion) => typeof suggestion === 'string' && suggestion.trim())

                Mixpanel.track(
                    isVagueSubject
                        ? constants.Mixpanel.LANDING_PAGE_COURSE_VALIDATION_FAILED_TOO_VAGUE
                        : constants.Mixpanel.LANDING_PAGE_COURSE_VALIDATION_FAILED_INVALID
                )

                setIsUnsupportedCourse(true)
                setRejectedGoalValue(goalValue.trim())
                setUnsupportedCourseMessage(isVagueSubject ? 'Sorry, that course is too vague.' : '')
                setVagueCourseSuggestions(
                    isVagueSubject
                        ? hasValidSuggestions
                            ? suggestions.map((suggestion) => suggestion.trim())
                            : fallbackSuggestions
                        : []
                )
                setSlicingIndex(isVagueSubject ? 5 : defaultSlicingIndex)
                setUnsupportedAttempt((attempt) => attempt + 1)
                setShowTitle(true)
                setScrollToTop(false)
                return
            }

            toast.error('Failed to start your session. Please try again.')
            return
        } finally {
            setIsStartingSession(false)
        }

        Mixpanel.people.set({
            'skillName': skillName
        })
        Mixpanel.track(constants.Mixpanel.SKILL_NAME)
        navigate(`/onboarding/1`)
    }

    const handleSkillSelection = (index) => {
        Mixpanel.track(constants.Mixpanel.LANDING_PAGE_POPULAR_COURSE_CLICKED)
        setShowTitle(true)
        setScrollToTop(false)
        const tempArray = [...Array(selectedSkill.length).fill(false)]
        tempArray[index] = true
        setSelectedSkill(tempArray)
        setGoalValue(popularSkills[index])
    }

    const handleVagueSuggestionSelection = (suggestion) => {
        Mixpanel.track(constants.Mixpanel.LANDING_PAGE_COURSE_SUGGESTION_CLICKED)
        setShowTitle(true)
        setScrollToTop(false)
        setGoalValue(suggestion)
    }

    const handleDropResults = (index) => {
        if (inputRef.current) {
            inputRef.current.focus()
        }
        setShowTitle(false)
        setGoalValue(searchResult[index])
    }
    console.log(width, height)


    const handleRoleSearch = () => {
        Mixpanel.track(constants.Mixpanel.LANDING_PAGE_SEARCH_BY_JOB_INSTEAD)
        navigate('/roles/search')
    }

    const hasChangedRejectedCourse = isUnsupportedCourse
        && goalValue.trim() !== rejectedGoalValue
    const showCourseInputError = isUnsupportedCourse && !hasChangedRejectedCourse

    return (
        <>
            {step === 0 && <div className='w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative text-center flex flex-col justify-between gap-2' onClick={(e) => {
                setShowTitle(true)
                setScrollToTop(false)
            }}>
                {/* <div className=''>
                <h1 className={`text-[2.375rem] lg:text-[2.75rem] ${height <= 740 ? 'pt-3' : 'pt-8'} pb-2 leading-9`}>Achieve Your Goals <span className='text-[#00C1FF] font-bold'>3x Faster</span></h1>
                <p className='lg:text-2xl opacity-80 leading-4'>Analyse your skills & get the best {(course_slug && popularSkillsFromCategories.hasOwnProperty(course_slug)) ? popularSkillsFromCategories[course_slug]?.name : skill_slug ? skill_slug : "" } course personalised just for you!</p>
            </div> */}
                <div className={`mt-8 flex md:flex-1 flex-col ${isUnsupportedCourse ? 'gap-8' : 'gap-12'}`}>
                    <div className='w-full flex flex-col justify-center items-center gap-2'>
                        <h1
                            className='w-full font-medium text-[1.75rem] md:text-[1.75rem] leading-[130%] bg-gradient-to-r from-[#FFFFFF] to-[#FFE994] bg-clip-text text-transparent text-left md:text-center'
                            role={isUnsupportedCourse ? 'alert' : undefined}
                            aria-live={isUnsupportedCourse ? 'polite' : undefined}
                        >
                            {isUnsupportedCourse ? (
                                unsupportedCourseMessage
                                    ? <>Sorry, that course is <span className='font-bold'>too vague.</span></>
                                    : <>Sorry, we <span className='font-bold'>can’t create that course.</span></>
                            ) : (
                                <>Build Job-Ready Skills With A <span className='font-bold'>Certified Course</span></>
                            )}
                        </h1>
                        {!isUnsupportedCourse && (
                            <p className='w-full font-medium text-[0.875rem] leading-5 text-white/60 text-left md:text-center'>
                                Personalised for your level in under 60 seconds!
                            </p>
                        )}
                        {unsupportedCourseMessage && (
                            <div className='mt-4 w-full'>
                                <p className='text-[1rem] text-left font-medium'>Try something like:</p>
                                <div className='mt-2 flex w-full flex-col gap-2' role='group' aria-label='Suggested course names'>
                                    {vagueCourseSuggestions.map((suggestion) => (
                                        <button
                                            key={suggestion}
                                            type='button'
                                            className={`min-h-[46px] w-full cursor-pointer rounded-[0.5rem] border border-[#406AFF]/50 px-3 text-center text-[0.75rem] font-medium transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#70AEFF] ${goalValue === suggestion ? 'bg-[#406AFF] text-white' : 'bg-[#406AFF]/15 text-[#70AEFF] hover:bg-[#406AFF]/25'}`}
                                            aria-pressed={goalValue === suggestion}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleVagueSuggestionSelection(suggestion)
                                            }}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                    <div className={`flex md:flex-1 flex-col justify-center`}>
                        <label htmlFor='course-subject' className='text-[1rem] text-left font-medium'>
                            {unsupportedCourseMessage
                                ? 'Or create your own personalised course:'
                                : isUnsupportedCourse
                                    ? 'What Else Would You Like To Learn?'
                                    : 'What Do You Want To Learn?'}
                        </label>
                        <form onSubmit={handleSubmit}>
                            <div
                                key={unsupportedAttempt}
                                className={`course-subject-control relative mt-2 flex h-[3.5625rem] w-full items-center rounded-[0.75rem] bg-[#406AFF]/10 ${showCourseInputError ? 'course-subject-error course-subject-shake' : 'box-glow'}`}
                            >
                                <span className='pointer-events-none absolute inset-y-0 left-3 flex items-center text-white/60'>
                                    <FontAwesomeIcon icon={faMagnifyingGlass} className='text-sm' />
                                </span>
                                <input
                                    id='course-subject'
                                    type="text"
                                    ref={inputRef}
                                    className='h-full min-w-0 flex-1 border-0 bg-transparent pl-9 pr-3 text-[0.875rem] text-white outline-none placeholder:text-sm placeholder-white/30 focus:border-0 focus:outline-none focus:ring-0'
                                    placeholder='For e.g. Data Science, Java, etc.'
                                    value={goalValue}
                                    aria-invalid={showCourseInputError}
                                    onFocus={() => {
                                        setShowTitle(false)
                                    }}
                                    onChange={(e) => {
                                        if (showTitle) {
                                            setShowTitle(false)
                                        }
                                        setGoalValue(e.target.value)
                                    }}
                                />
                                {!showCourseInputError && (
                                    <button
                                        type="submit"
                                        disabled={!goalValue || isStartingSession}
                                        className='mr-3 flex h-[2.0625rem] shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[#7FC241] px-4 text-sm font-semibold text-black disabled:opacity-50'
                                    >
                                        {isStartingSession ? (
                                            <>
                                                Curating
                                                <LoaderCircle className='h-4 w-4 animate-spin' aria-hidden='true' />
                                            </>
                                        ) : (
                                            <>
                                                Start
                                                <ArrowRight className='h-4 w-4' aria-hidden='true' />
                                            </>
                                        )}
                                        {/* <FontAwesomeIcon icon={faArrowRight} className='text-base arrow-move' /> */}
                                    </button>
                                )}
                            </div>
                        </form>

                        {(!showTitle && goalValue) && <div className='mt-4 rounded-[0.75rem]'>
                            {searchResult.length <= 0 ? <div className='h-[7.125rem] w-full rounded-[0.75rem]'>
                                <SearchSkeleton />
                            </div> : <div className='rounded-[0.75rem] border border-[#7FC241] h-[7.125rem] pl-2 pr-4 py-2 overflow-y-scroll' style={{ background: 'linear-gradient(to right, rgba(7, 25, 51, 0.8) 0%, rgba(8, 47, 89, 0.8) 55%, rgba(7, 25, 51, 0.8) 100%)' }}>
                                {searchResult?.map((item, index) => (
                                    <div key={index} onClick={() => handleDropResults(index)} className={`text-left ${goalValue === item ? '' : ''} text-[#70AEFF] font-medium text-[0.75rem] px-3 py-2.5 border-b border-white/10 flex justify-between items-center`}>
                                        {item}
                                        <ArrowUpRight className='w-4 h-4 font-bold text-[#70AEFF]/35' strokeWidth={2.5} />
                                    </div>
                                ))}
                            </div>}
                        </div>}
                        {(showTitle || !goalValue) && <>
                            <div className={`flex justify-between items-center ${height <= 740 ? 'mt-6' : 'mt-6'} mb-2 text-[#90BEED]`}>
                                <p className='text-[0.875rem] font-medium text-white/50'>
                                    {isUnsupportedCourse ? 'POPULAR COURSES TO HELP GROW YOUR CAREER' : 'POPULAR SKILLS TO START WITH'}
                                </p>
                                {/* <Link to='/course-page'><p className='underline text-xs'>Explore By Category?</p></Link> */}
                            </div>
                            <div className={`${popularSkills?.length < 3 ? '' : 'takeaway'}`}>
                                <div className='flex gap-2 flex-wrap items-center'>
                                    {popularSkills?.slice(0, slicingIndex)?.map((item, index) => {

                                        const skill_count = currentPopularSkillsCount.find(skill => skill.subject_name?.toLowerCase() === item?.toLowerCase())?.total_count
                                        return (
                                            (
                                                <div key={index} className={`${goalValue === item ? 'bg-[#406AFF]' : 'bg-[#406AFF]/15 text-[#9ABFE5] text-[#70AEFF]'}  border border-[#406AFF]/50 h-[46px] flex items-center justify-center px-3 cursor-pointer rounded-[0.5rem] text-[0.75rem] font-medium relative`} onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleSkillSelection(index)
                                                }}>
                                                    {item}
                                                    <div className={`absolute top-0 right-0 flex justify-center items-center gap-1 rounded-tr-[0.5rem] rounded-bl-[0.5rem] ${goalValue === item ? 'bg-white/20' : 'bg-[#406AFF]/15'} text-white/70 text-[9px] font-medium leading-none px-1 py-[3px]`}>
                                                        <img src="https://img.learntube.ai/public-assets/personalise/Vector%20(15).webp" alt="" width={7} height={7} />{formatSkillCount(skill_count)}</div>
                                                </div>
                                            )
                                        )
                                    })}
                                    {
                                        slicingIndex < popularSkills?.length ? <div className='cursor-pointer text-[0.75rem] font-semibold text-[#70AEFF] underline underline-offset-2' onClick={() => setSlicingIndex(popularSkills?.length)}>+{popularSkills?.length - slicingIndex} more</div> :
                                            <div className='cursor-pointer text-[0.75rem] font-semibold text-[#70AEFF] underline underline-offset-2' onClick={() => setSlicingIndex(unsupportedCourseMessage ? 5 : width <= 768 ? 5 : 10)}>Show Less</div>
                                    }
                                </div>
                            </div>
                        </>}

                        {(showTitle || !goalValue) &&
                            <div className='mt-4 w-full text-left flex justify-start items-center gap-1'>
                                <div className='text-[0.875rem] text-left font-normal text-white/50'>Not sure what to learn?</div>
                                <div className='cursor-pointer text-[0.875rem] font-semibold text-[#7FC241] underline underline-offset-2' onClick={handleRoleSearch}>Search by job instead</div>
                            </div>}
                    </div>
                </div>
                <div className='flex flex-col gap-3 w-full min-w-0 mt-6 overflow-hidden border-t border-white/20 pt-4'>
                    <p className='font-semibold text-[1rem] leading-normal text-center bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent break-words'>
                        Turning Skills Into Careers
                    </p>
                    <div className='w-full min-w-0 overflow-hidden'>
                        <Swiper
                            spaceBetween={6}
                            slidesPerView={3}
                            className='!overflow-hidden swiper-equal-height'
                        >
                            {[
                                { value: '28L+', label: 'Active Learners' },
                                { value: '4.8/5', label: 'Course Rating' },
                                { value: '1,100+', label: 'Companies Hiring' },
                            ].map((stat, index) => (
                                <SwiperSlide key={index}>
                                    <div className='h-full bg-[#406AFF]/10 flex flex-col justify-start gap-2 p-3 overflow-hidden text-left'>
                                        <p className='font-bold text-[1.25rem] leading-none text-[#70aeff] '>{stat.value}</p>
                                        <p className='font-normal text-[0.75rem] leading-[1.4] text-[#70aeff]/50'>{stat.label}</p>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </div>}

            {/* {step === 2 &&
            <div className='text-center max-w-[1440px] text-white flex-1 p-4 lg:pb-6 lg:px-10 relative flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-5 mx-auto'>
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className='absolute z-[999] -top-8 lg:-top-10 left-4 lg:left-12 text-2xl lg:text-3xl cursor-pointer'
                    onClick={() => setStep(1)}
                />
                <div className='lg:flex-1'>
                    <div className={`${height <= 720 ? 'w-[80%]' : 'w-full'} md:w-[50%] h-auto mx-auto pl-2`}>
                        <img src="https://img.learntube.ai/public-assets/AI_Syllabus_generator/online-course_1.png?updatedAt=1685205758186" width={320} height={213} alt="" className='w-full h-full'/>
                    </div>
                    <h2 className={`${height <= 720 ? 'text-xl lg:text-[44px]' : 'text-2xl lg:text-[44px]'} leading-6 lg:leading-[50px]`}>Let’s Create The Best <span className='font-bold text-[#00C1FF]'>{capitalise(goalValue)} Course</span> For You!</h2>
                </div>
                <div className='flex-1 flex-shrink-0 flex flex-col justify-center lg:justify-start pb-10 lg:pb-0'>
                    <h2 className={`${height <= 720 ? 'text-xl mt-5 lg:text-2xl' : 'text-2xl'} leading-6 font-bold text-white text-left mb-3`}>What’s your current skill level?</h2>
                    <div className='flex flex-col gap-2.5'>
                        {options.map((option) => (
                            <div className={`cursor-pointer w-full h-[45px] md:h-[64px] flex justify-between items-center rounded-xl hover:bg-[#517EAA] bg-[#2D4F71]`} key={option.id} onClick={() => handleChange(option)}>
                                <div className='flex items-center'>
                                    <div className='w-4 md:w-6 h-4 md:h-6 mx-3 border md:border-2 border-[#7FC241] rounded-full flex justify-center items-center'>
                                        <div className={`w-2 md:w-3 h-2 md:h-3 rounded-full`}/>
                                    </div>
                                    <p className={`${height <= 720 ? 'text-lg lg:text-xl' : 'text-xl'} font-medium`}>{option.description}</p>
                                </div>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className={`text-[#DBFF00] text-xl mr-4 font-black hidden`}
                                />
                            </div>
                        ))}   
                    </div>
                </div>
            </div>
        } */}
        </>
    )
}

export default LandingPageV2
